// FILE: src/utils/firebase/features/budgets/writers.js

import { getDbOrThrow } from "../../core/firestoreCore";
import { COLLECTIONS, slugify } from "../../core/collections";
import { invalidate as _invalidate } from "../../cache/getOrFetch";

import {
  collection,
  doc,
  serverTimestamp,
  setDoc,
  updateDoc,
  getDoc,
  getDocs,
  deleteDoc,
  writeBatch,
  query,
  where,
  orderBy,
  limit,
} from "firebase/firestore";

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */

function normalizeString(s) {
  return String(s || "").trim();
}

function ensureSource(src) {
  const type = String(src?.type || "networth").trim().toLowerCase();
  if (type === "account") {
    return { type: "account", accountId: src?.accountId ? String(src.accountId) : null };
  }
  return { type: "networth" };
}

/** yyyy-MM-dd for consistent string compare with stored dates */
function todayISO() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}
const lc = (s) => String(s || "").trim().toLowerCase();

/**
 * shapeBudgetItem
 * Builds a stable budget payload. `slug` is immutable; `budgetTagPath` is derived from slug.
 */
export function shapeBudgetItem(p = {}) {
  const name = normalizeString(p.name);
  const baseSlug = normalizeString(p.slug) || name;
  const slug = slugify(baseSlug); // stable + safe
  const budgetTagPath = [`budget / ${slug}`]; // stable tag path for tx default tagging

  const amountNum = Number(p.amount ?? 0);
  const bufferPctNum = Number(p.bufferPct ?? 10);

  return {
    name,
    slug,
    budgetTagPath,               // do not mutate after create
    targetDate: normalizeString(p.targetDate), // "YYYY-MM-DD"
    amount: Number.isFinite(amountNum) ? amountNum : 0,
    bufferPct: Number.isFinite(bufferPctNum) ? bufferPctNum : 10,
    source: ensureSource(p.source),
    tags: Array.isArray(p.tags) ? p.tags.map(normalizeString).filter(Boolean) : [],
  };
}

function _invalidateBudgets(userEmail) {
  _invalidate(`budgets:${String(userEmail || "").toLowerCase()}`);
}

/* ------------------------------------------------------------------ */
/* Writers                                                            */
/* ------------------------------------------------------------------ */

/**
 * createBudgetItem
 * Creates a new budget doc under user_budgets.
 *
 * @param {{ user: { uid:string, email:string }, item: any }} param0
 * @returns {Promise<any>} created record
 */
export async function createBudgetItem({ user, item }) {
  if (!user?.uid) throw new Error("Not signed in.");
  const db = getDbOrThrow();
  const email = String(user.email || "").toLowerCase();

  const shaped = shapeBudgetItem(item);
  const ref = doc(collection(db, COLLECTIONS.userBudgets));
  const now = serverTimestamp();

  const record = {
    ...shaped,
    id: ref.id,
    userId: user.uid,
    userEmail: email,
    createdAt: now,
    updatedAt: now,
  };

  await setDoc(ref, record, { merge: true });
  _invalidateBudgets(email);
  return record;
}

/**
 * updateBudgetItem
 * Allows editing: name, targetDate, amount, bufferPct, source, tags.
 * Does NOT change: slug, budgetTagPath.
 *
 * @param {{ user: { uid:string, email:string }, item: any }} param0
 * @returns {Promise<any>} updated record (merged)
 */
export async function updateBudgetItem({ user, item }) {
  if (!user?.uid) throw new Error("Not signed in.");
  if (!item?.id) throw new Error("Missing budget id.");
  const db = getDbOrThrow();
  const email = String(user.email || "").toLowerCase();

  const ref = doc(db, COLLECTIONS.userBudgets, String(item.id));
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Budget not found.");

  // Preserve immutable fields from existing doc
  const prev = snap.data() || {};
  const shaped = shapeBudgetItem(item);

  const patch = {
    // editable fields
    name: shaped.name,
    targetDate: shaped.targetDate,
    amount: shaped.amount,
    bufferPct: shaped.bufferPct,
    source: shaped.source,
    tags: shaped.tags,
    // immutable fields preserved
    slug: prev.slug,
    budgetTagPath: Array.isArray(prev.budgetTagPath) ? prev.budgetTagPath : [`budget / ${prev.slug}`],
    // meta
    updatedAt: serverTimestamp(),
  };

  await updateDoc(ref, patch);
  _invalidateBudgets(email);

  return { ...(prev || {}), ...patch, id: ref.id, userId: prev.userId, userEmail: prev.userEmail };
}

/**
 * deleteBudgetItem
 * Deletes a budget doc AND any *future-dated* planned transactions associated with it.
 * Association rules (OR):
 *   - tx.budgetId === budget.id
 *   - tx.tagPath (or tagPaths) contains the canonical budget tag "budget / <slug>"
 *
 * @param {{ user: { uid:string, email:string }, id: string }} param0
 * @returns {Promise<{id:string, deletedTxCount:number}>}
 */
export async function deleteBudgetItem({ user, id }) {
  if (!user?.uid) throw new Error("Not signed in.");
  if (!id) throw new Error("Missing budget id.");
  const db = getDbOrThrow();
  const email = String(user.email || "").toLowerCase();
  const budgetId = String(id);

  // 1) Load the budget to get the canonical budget tag (if exists)
  const bRef = doc(db, COLLECTIONS.userBudgets, budgetId);
  const bSnap = await getDoc(bRef);
  const bData = bSnap.exists() ? (bSnap.data() || {}) : null;
  const budgetTag =
    bData && Array.isArray(bData.budgetTagPath) && bData.budgetTagPath.length
      ? lc(bData.budgetTagPath[0])
      : "";

  // 2) Load a *future* slice of user transactions and filter matches client-side.
  // Try indexed query first (userEmail == AND orderBy date), fallback to simpler query if index missing.
  const txCol = collection(db, COLLECTIONS.userTransactions);
  const today = todayISO();
  let txSnap;

  try {
    const qy = query(
      txCol,
      where("userEmail", "==", email),
      orderBy("date", "asc"),
      limit(5000)
    );
    txSnap = await getDocs(qy);
  } catch {
    // Fallback: no orderBy (avoid index issues); still capped
    const qy = query(
      txCol,
      where("userEmail", "==", email),
      limit(5000)
    );
    txSnap = await getDocs(qy);
  }

  const toDeleteIds = new Set();
  txSnap.forEach((d) => {
    const row = d.data() || {};
    // Only consider future-dated tx (planned)
    const dateStr = String(row?.date || "");
    if (!dateStr || dateStr < today) return;

    const matchById = String(row?.budgetId || "") === budgetId;

    const t1 = Array.isArray(row?.tagPath) ? row.tagPath : [];
    const t2 = Array.isArray(row?.tagPaths) ? row.tagPaths : [];
    const hasBudgetTag =
      !!budgetTag &&
      ([...t1, ...t2].some((t) => lc(t) === budgetTag));

    if (matchById || hasBudgetTag) toDeleteIds.add(d.id);
  });

  // 3) Batch delete: related tx first, then the budget doc
  const batch = writeBatch(db);
  for (const txId of toDeleteIds) {
    batch.delete(doc(db, COLLECTIONS.userTransactions, txId));
  }
  batch.delete(bRef);
  await batch.commit();

  _invalidateBudgets(email);
  return { id: budgetId, deletedTxCount: toDeleteIds.size };
}
